<script lang="ts" setup>
import {
  PopoverColorEnum,
  PopoverPositionEnum,
  PopoverPositionType,
  TirPmPopover,
} from 'tir-pm-popover'
import { computed, onMounted, PropType, ref, useSlots, watch } from 'vue'

/**Параметры */
const props = defineProps({
  /**Выбранный цвет */
  modelValue: { type: String },
  /**Позиция */
  position: {
    type: String as PropType<PopoverPositionEnum>,
    default: PopoverPositionEnum.Auto,
  },
  /**Кастомная позиция */
  customPosition: {
    type: Object as PropType<PopoverPositionType>,
  },
  /**Цвет поповера */
  popoverColor: {
    type: String as PropType<PopoverColorEnum>,
    default: PopoverColorEnum.White,
  },
  /**Ширина */
  width: { type: String, default: '11.5rem' },
  /**Классы контента */
  classes: { type: String, default: '' },
  /**Скрыть треугольник поповера */
  isHideTriangle: { type: Boolean, default: true },
  /**Отключение потери фокуса при клике по контенту */
  disableContentBlur: { type: Boolean, default: false },
  /**Внешний список цветов */
  colors: { type: Array as PropType<string[]> },
})
/**События */
const emit = defineEmits<{
  /**Обновление modelValue значения */
  (e: 'update:modelValue', color: string): void
  /**Выбор цвета из палитры */
  (e: 'select', color: string): void
}>()
/**Слоты */
const slots = useSlots()

/**Компонент поповер */
const $popover = ref<InstanceType<typeof TirPmPopover>>()

/**Список доступных цветов */
const colors = computed(() => {
  if (props.colors?.length) {
    return props.colors
  } else {
    return [
      '#0AA4D4',
      '#4480E7',
      '#2233CE',
      '#121AE8',
      '#9D12DE',
      '#8330EC',
      '#5202B8',
      '#4A00C3',
      '#E72381',
      '#CE0000',
      '#B20036',
      '#AE0054',
      '#EA8313',
      '#DD5D00',
      '#B24000',
      '#D03E00',
      '#5ACC00',
      '#00B955',
      '#039000',
      '#5F8000',
    ]
  }
})
/**Есть ли дефолтный слот */
const isDefaultSlot = computed(() => slots?.default?.())

/**Отслеживание изменений параметра modelValue */
watch(() => props.modelValue, checkDefaultValue)

/**После рендера */
onMounted(() => checkDefaultValue())

/**Проверка верного значения цвета, иначе отправляется первый цвет */
function checkDefaultValue() {
  if (!props.modelValue) {
    emit('update:modelValue', colors.value[0])
  }
}
/**Выбор цвета */
const selectColor = (color: string) => {
  if (color != props.modelValue) {
    emit('update:modelValue', color)
    emit('select', color)
    $popover.value?.close?.()
  }
}
</script>
<template>
  <TirPmPopover
    class="tir-pm-color-palette"
    ref="$popover"
    :color="popoverColor"
    :position="position"
    :customPosition="customPosition"
    :width="width"
    :classes="classes"
    :isHideTriangle="isHideTriangle"
    :disableContentBlur="disableContentBlur"
  >
    <template #default>
      <slot v-if="isDefaultSlot" />
      <slot
        v-else
        name="icon"
        :style="{ color: modelValue }"
        :color="modelValue"
      >
        <div
          class="tir-pm-color-palette_block"
          :style="{ 'background-color': modelValue }"
        />
      </slot>
    </template>
    <template #content>
      <div class="tir-pm-color-palette_list">
        <div
          v-for="color in colors"
          class="tir-pm-color-palette_list_item"
          :class="{ active: color == modelValue, 'background-color': color }"
          @click="selectColor(color)"
        >
          <slot name="icon" :style="{ color: color }" :color="color">
            <div
              class="tir-pm-color-palette_block"
              :style="{ 'background-color': color }"
            />
          </slot>
        </div>
      </div>
    </template>
  </TirPmPopover>
</template>
<style lang="scss" scoped>
.tir-pm-color-palette {
  &_list {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.5rem;
    max-width: 11.5rem;
    padding: 0.75rem 1rem;

    &_item {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: 0.1s ease;

      &:hover,
      &.active {
        background: var(--neutral-translucent-5, rgba(39, 45, 55, 0.05));
      }
    }
  }
  &_block {
    border-radius: 50%;
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }
}
</style>
